import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { cityCoordinates } from '@/lib/utils/coordinates';
import { useTheme } from '@/contexts/ThemeContext';

interface CitiesPlayedMapProps {
  cities: string[];
  className?: string;
  highlightedCity?: string | null;
}

const CitiesPlayedMap: React.FC<CitiesPlayedMapProps> = ({ cities, className, highlightedCity }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const { isDarkMode } = useTheme();

  // Filter cities to only those with coordinates
  const validCities = cities.filter(city => {
    const normalizedCity = city.split(',')[0].trim();
    return !!cityCoordinates[normalizedCity];
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (mapRef.current) {
      observer.observe(mapRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Combine internal hover state with external highlighted city
  const activeCity = hoveredCity || highlightedCity;

  if (validCities.length === 0) {
    return (
      <div className={cn(
        "bg-dark-300 rounded-xl p-4 text-center",
        "dark:bg-dark-300 dark:border-dark-400",
        "light:bg-light-200 light:border-light-300",
        "border",
        className
      )}>
        <p className="text-dark-800 dark:text-dark-800 light:text-light-600">
          No cities played yet. Start playing to see your global footprint!
        </p>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef}
      className={cn(
        "relative overflow-hidden rounded-xl border",
        "bg-dark-300 border-dark-400",
        "dark:bg-dark-300 dark:border-dark-400",
        "light:bg-light-200 light:border-light-300",
        "aspect-[16/9]",
        className
      )}
    >
      {/* World Map Background */}
      <div className="absolute inset-0 opacity-20 dark:opacity-20 light:opacity-10">
        <img 
          src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg" 
          alt="World Map" 
          className={cn(
            "w-full h-full object-contain",
            isDarkMode ? "opacity-50" : "opacity-30"
          )}
        />
      </div>

      {/* City Pins */}
      {validCities.map((city, index) => {
        const normalizedCity = city.split(',')[0].trim();
        const coords = cityCoordinates[normalizedCity];
        
        if (!coords) return null;
        
        // Convert lat/lng to x/y coordinates (simple mercator projection)
        const x = ((coords.lng + 180) / 360) * 100; // Convert to percentage
        const y = ((90 - coords.lat) / 180) * 100; // Convert to percentage
        
        return (
          <motion.div
            key={`${normalizedCity}-${index}`}
            className="absolute"
            style={{ 
              left: `${x}%`, 
              top: `${y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={isVisible ? {
              scale: 1,
              opacity: activeCity && normalizedCity !== activeCity ? 0.5 : 1,
              y: [0, -10, 0]
            } : { scale: 0, opacity: 0 }}
            transition={{ 
              delay: index * 0.1,
              duration: 0.5,
              y: {
                delay: index * 0.1 + 0.5,
                duration: 1,
                repeat: 0,
                ease: "easeInOut"
              }
            }}
            onMouseEnter={() => setHoveredCity(normalizedCity)}
            onMouseLeave={() => setHoveredCity(null)}
          >
            <div className="relative group">
              <MapPin 
                className={cn(
                  "h-4 w-4 text-gold-400 drop-shadow-lg",
                  "transform transition-colors duration-300",
                  "group-hover:text-gold-300",
                  normalizedCity === activeCity && "text-gold-300 animate-pulse"
                )} 
              />
              
              {/* Ping animation */}
              <div className={cn(
                "absolute inset-0 rounded-full",
                "bg-gold-400/50",
                normalizedCity === activeCity ? "opacity-100" : "opacity-0",
                "transition-opacity duration-700 z-10",
                normalizedCity === activeCity && "animate-[ping_3s_ease-in-out_infinite]"
              )} />
              
              {/* Glow effect */}
              <div className={cn(
                "absolute -inset-2 rounded-full",
                "bg-gold-400/30",
                "blur-md",
                normalizedCity === activeCity ? "opacity-100" : "opacity-0",
                "transition-opacity duration-700"
              )} />
              
              {/* City name tooltip */}
              <div className={cn(
                "absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full",
                "whitespace-nowrap px-2 py-1 rounded text-xs",
                "bg-dark-200 text-white border border-dark-400",
                "transition-opacity duration-200",
                normalizedCity === activeCity ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                "pointer-events-none z-10"
              )}>
                {normalizedCity}
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* Legend */}
      <div className={cn(
        "absolute bottom-2 left-2 p-2 rounded",
        "backdrop-blur-sm z-20",
        "border",
        "text-xs",
        "dark:bg-dark-200/90 dark:border-dark-400/80 dark:text-white",
        "light:bg-light-100/80 light:border-light-300 light:text-gray-900"
      )}>
        <div className="flex items-center">
          <MapPin className={cn(
            "h-3 w-3 mr-1",
            "dark:text-gold-400 drop-shadow-[0_0_2px_rgba(255,201,51,0.5)]",
            "light:text-gold-600"
          )} />
          <span>{validCities.length} {validCities.length === 1 ? 'City' : 'Cities'} Played</span>
        </div>
      </div>
    </div>
  );
};

export default CitiesPlayedMap;